<template>
  <div>
      <!-- Table Container Card -->
      <b-card
        no-body
        class="mb-0"
      >
        <div class="m-2">
          <b-row>
            <b-col
              cols="12"
              md="6"
              class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
            >
              <label>{{ $t('DEBTIA_SHOWING') }}</label>
              <v-select
                v-model="perPage"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :clearable="false"
                class="search_by_select per-page-selector d-inline-block mx-50"
                :options="perPageOptions"
              />
              <label>{{ $t('DEBTIA_ENTRIES') }}</label>
            </b-col>
            <b-col
              cols="12"
              md="6"
              class="query-input"
            >
              <div class="d-flex align-items-center justify-content-end">
                <b-form-input
                  v-model="searchQuery"
                  class="d-inline-block mr-1"
                  :placeholder="$t('DEBTIA_CASES_SEARCH_PLACEHOLDER')"
                />
                <b-button
                  v-if="clientSubscription"
                  variant="primary"
                  :to="{ name: 'case-create' }"
                >
                  <span class="text-nowrap">{{ $t('DEBTIA_ADD_CASE') }}</span>
                </b-button>
                <b-button
                  v-else
                  v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                  variant="primary"
                  @click="showAlert"
                >
                  <span class="text-nowrap">{{ $t('DEBTIA_ADD_CASE') }}</span>
                </b-button>
              </div>
            </b-col>
          </b-row>
        </div>
        <b-table
          ref="refUserListTable"
          class="position-relative"
          responsive
          selectable
          :fields="tableFields"
          :select-mode="selectMode"
          :items="formattedCasesData"
          :per-page="perPage"
          :current-page="currentPage"
          hover
          @row-selected="onRowSelected"
        >
          <template #cell(debtorName)="data">
            <span class="text-truncate d-inline-block" style="max-width: 200px; font-weight: 400;" :title="data.item.debtorName">
              {{ data.item.debtorName }}
            </span>
          </template>
          <template #cell(principal)="data">
            {{ formatCurrency(data.item.principal) }}
          </template>
          <template #cell(paidAmount)="data">
            <span :class="data.item.paidAmount > 0 ? 'text-success' : ''">
              {{ formatCurrency(data.item.paidAmount) }}
            </span>
          </template>
          <template #cell(remainingAmount)="data">
            <span :class="data.item.remainingAmount > 0 ? 'text-danger font-weight-bold' : 'text-success font-weight-bold'">
              {{ formatCurrency(data.item.remainingAmount) }}
            </span>
          </template>
          <template #cell(status)="data">
            <b-badge
              v-if="data.item.status === 4"
              pill
              variant="warning"
            >
              {{ $t('DEBTIA_PAUSED') }}
            </b-badge>
            <b-badge
              v-if="data.item.status === 3"
              pill
              variant="danger"
            >
              {{ $t('DEBTIA_TERMINATED') }}
            </b-badge>
            <b-badge
              v-if="data.item.status === 2"
              pill
              variant="danger"
            >
              {{ $t('DEBTIA_DECLINED') }}
            </b-badge>
            <b-badge
              v-if="data.item.status === 1"
              pill
              variant="success"
            >
              {{ $t('DEBTIA_ACCEPTED') }}
            </b-badge>
            <b-badge
              v-if="data.item.status === 0 || data.item.status === null"
              pill
              variant="warning"
            >
              {{ $t('DEBTIA_PENDING') }}
            </b-badge>
          </template>
        </b-table>
        <div class="mx-2 mb-2">
          <b-row>
            <b-col
              cols="12"
              sm="6"
              class="d-flex align-items-center justify-content-center justify-content-sm-start"
            >
              <span class="text-muted">{{ $t('DEBTIA_SHOWING') }} {{ from }} {{ $t('DEBTIA_COMMON_TO') }} {{ to }} {{ $t('DEBTIA_OF') }} {{ totalRows }} {{ $t('DEBTIA_ENTRIES') }}</span>
            </b-col>
            <!-- Pagination -->
            <b-col
              cols="12"
              sm="6"
              class="d-flex align-items-center justify-content-center justify-content-sm-end"
            >
              <b-pagination
                v-if="dataLoaded"
                v-model="currentPage"
                :total-rows="totalRows"
                :per-page="perPage"
                first-number
                last-number
                class="mb-0 mt-1 mt-sm-0"
                prev-class="prev-item"
                next-class="next-item"
              >
                <template #prev-text>
                  <feather-icon
                    icon="ChevronLeftIcon"
                    size="18"
                  />
                </template>
                <template #next-text>
                  <feather-icon
                    icon="ChevronRightIcon"
                    size="18"
                  />
                </template>
              </b-pagination>

            </b-col>

          </b-row>
        </div>
        <cases-modal
          ref="caseModalRef"
          :modal-loads="modalLoader"
          :reload-method="getCases"
          :default-currency="defaultCurrency"
        />
      </b-card>
  </div>
</template>

<script>
import {
  BCard, BTable, BRow, BCol, BFormInput, BButton, BPagination, BBadge,
} from 'bootstrap-vue'
import axios from '@axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import vSelect from 'vue-select'
import constants from '@/app-configs/constants'
import i18n from '@/libs/i18n'
import Ripple from 'vue-ripple-directive'
import CasesModal from './CasesModal.vue'

export default {
  components: {
    BCard,
    BTable,
    BRow,
    BCol,
    vSelect,
    BFormInput,
    BButton,
    BBadge,
    BPagination,
    CasesModal,
  },
  directives: {
    Ripple,
  },
  data() {
    const q = this.$route.query
    return {
      loader: false,
      casesData: [],
      perPage: parseInt(q.perPage, 10) || 10,
      perPageOptions: [10, 25, 50, 100],
      searchQuery: q.q || '',
      currentPage: parseInt(q.page, 10) || 1,
      dataLoaded: false,
      editData: {},
      addCaseInput: {
        id: 0,
        name: '',
        description: '',
        user_id: JSON.parse(localStorage.getItem('userData')).data.id,
      },
      caseId: 0,
      user_type: JSON.parse(localStorage.getItem('userData')).data.user_type,
      modalLoader: true,
      constants,
      isSuperAdmin: false,
      wordFile: false,
      finalStep: false,
      showVariables: false,
      busy: false,
      clicked: false,
      selectMode: 'single',
      defaultCurrency: 'DKK',
      selected: [],
      clientSubscription: false,
      maxLimit: 'no-active-subscription',
    }
  },
  computed: {
    tableFields() {
      return [
        { key: 'id', sortable: true, label: this.$t('DEBTIA_CASES_FIELD_CASE_NO') },
        { key: 'debtorName', label: this.$t('DEBTIA_CASES_FIELD_DEBTOR') },
        { key: 'debtorCvrCpr', label: this.$t('DEBTIA_CASES_FIELD_CVRCPR') },
        { key: 'principal', label: this.$t('DEBTIA_CASES_FIELD_TO_COLLECT'), class: 'text-right' },
        { key: 'paidAmount', label: this.$t('DEBTIA_STATUS_PAID'), class: 'text-right' },
        { key: 'remainingAmount', label: this.$t('DEBTIA_CASES_FIELD_REMAINING'), class: 'text-right' },
        { key: 'status', label: this.$t('DEBTIA_STATUS') },
        { key: 'createdDate', label: this.$t('DEBTIA_CASES_FIELD_CREATED'), sortable: true },
      ]
    },
    formattedCasesData() {
      const mapped = this.casesData.map(c => {
        const mainDebtor = (c.get_debtors && c.get_debtors.length > 0) ? c.get_debtors[0] : null
        // Brug v2 API felter direkte (fra BC statements)
        const principal = parseFloat(c.total_debt) || 0
        const paidAmount = parseFloat(c.total_paid_amount) || 0
        const remainingAmount = parseFloat(c.total_remaining_debt) || 0
        return {
          ...c,
          debtorName: mainDebtor ? mainDebtor.name : '-',
          debtorCvr: mainDebtor ? (mainDebtor.cvr_number || '') : '',
          debtorCpr: mainDebtor ? (mainDebtor.cpr || '') : '',
          debtorCvrCpr: mainDebtor ? (mainDebtor.cvr_number || mainDebtor.cpr || '-') : '-',
          principal,
          paidAmount,
          remainingAmount,
          createdDate: c.created_at ? new Date(c.created_at).toLocaleDateString('da-DK') : '-',
        }
      })
      if (!this.searchQuery) return mapped
      const q = this.searchQuery.toLowerCase()
      return mapped.filter(c => String(c.id).includes(q)
        || c.debtorName.toLowerCase().includes(q)
        || c.debtorCvr.toLowerCase().includes(q)
        || c.debtorCpr.toLowerCase().includes(q)
        || (c.bc_case_id && c.bc_case_id.toLowerCase().includes(q)))
    },
    totalRows() {
      return this.formattedCasesData.length
    },
    from() {
      if (this.totalRows === 0) return 0
      return ((this.currentPage - 1) * this.perPage) + 1
    },
    to() {
      const end = this.currentPage * this.perPage
      return end > this.totalRows ? this.totalRows : end
    },
  },
  watch: {
    perPage() {
      if (!this.dataLoaded) return
      this.currentPage = 1
      this.syncQueryParams()
    },
    searchQuery() {
      if (!this.dataLoaded) return
      this.currentPage = 1
      this.syncQueryParams()
    },
    currentPage() {
      if (!this.dataLoaded) return
      this.syncQueryParams()
    },
    loader: {
      immediate: true,
      handler(val) {
        this.$store.commit('app/SET_GLOBAL_LOADING', { show: val, text: this.$t('DEBTIA_LOADING_CASES') })
      },
    },
  },
  created() {
    this.getCases()
    this.getSettings()
  },
  beforeDestroy() {
    this.$store.commit('app/SET_GLOBAL_LOADING', false)
  },
  methods: {
    syncQueryParams() {
      const query = {}
      if (this.currentPage > 1) query.page = this.currentPage
      if (this.perPage !== 10) query.perPage = this.perPage
      if (this.searchQuery) query.q = this.searchQuery
      // Use replace to avoid polluting browser history with every keystroke
      this.$router.replace({ query }).catch(() => {})
    },
    showAlert() {
      if (this.maxLimit === 'allow-case') {
        this.$router.push({ name: 'case-create' })
      } else {
        const message = this.maxLimit === 'limit-reached' ? i18n.t('DEBTIA_MAXIMUM_CASES_REACHED') : i18n.t('DEBTIA_INACTIVE_SUBSCRIPTION_MSG')
        this.$swal({
          icon: 'warning',
          html: `
            <p>${message}</p>
            <div style="background: #f8f9fa; border-radius: 8px; padding: 16px; margin-top: 16px; text-align: left;">
              <div style="display: flex; align-items: center; gap: 10px; margin-bottom: 8px;">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#088c76" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>
                <a href="tel:${constants.supportPhone}" style="color: #088c76; font-weight: 700; text-decoration: none;">${constants.supportPhone}</a>
              </div>
              <div style="display: flex; align-items: center; gap: 10px;">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#088c76" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>
                <a href="mailto:${constants.supportEmail}" style="color: #088c76; font-weight: 700; text-decoration: none;">${constants.supportEmail}</a>
              </div>
            </div>
          `,
          showCloseButton: false,
          showCancelButton: false,
          focusConfirm: false,
          confirmButtonText: this.$t('DEBTIA_COMMON_OK'),
          customClass: {
            confirmButton: 'btn btn-primary',
            cancelButton: 'btn btn-outline-danger ml-1',
          },
          buttonsStyling: false,
        })
      }
    },
    onRowSelected(item) {
      this.selected = item
      this.$router.push(`/cases/details/${item['0'].id}`)
    },
    async getSettings() {
      try {
        const cases = await axios.get('/user-settings/get?key=default_currency')
        if (cases.status) {
          this.defaultCurrency = 'DKK'
          if (cases.data.data) {
            this.defaultCurrency = cases.data.data.value
          }
        }
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('DEBTIA_ERROR'),
            icon: 'CoffeeIcon',
            variant: 'danger',
            text: error,
          },
        })
      }
    },
    async getCases() {
      try {
        if (this.user_type === 'super-admin') {
          this.isSuperAdmin = true
        } else {
          this.isSuperAdmin = false
        }
        this.loader = true
        const cases = await axios.get(`${constants.serverBaseUrl}/api/v2/debtiamit/get-debt-cases-list?perPage=10000&page=1&sortDesc=true`)
        this.loader = false
        if (cases.status) {
          const responseData = cases.data.data || cases.data
          this.clientSubscription = responseData.client_subscriptions
          this.maxLimit = responseData.max_limit
          this.casesData = responseData.data || responseData
          this.dataLoaded = true
        }
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('DEBTIA_ERROR'),
            icon: 'CoffeeIcon',
            variant: 'danger',
            text: error,
          },
        })
      }
    },
    caseDetails(id) {
      this.busy = true
      this.caseId = id
    },
    addCase(item) {
      this.modalLoader = true
      this.wordFile = !this.wordFile
      this.finalStep = !this.finalStep
      this.showVariables = !this.showVariables
      this.editData = item
      this.modalLoader = false
      this.clicked = !this.clicked
      this.getSettings()
    },
    async deleteCase(id) {
      this.loader = true
      const cases = await axios.post(`${constants.baseUrl}/cases/delete`,
        { caseID: id })
      if (cases.status) {
        this.getCases()
      }
      this.loader = false
    },
    formatBaseType(baseType) {
      if (!baseType || !baseType.name) return ''
      // API returns "English (Dansk)" format - extract Danish part from parentheses
      const match = baseType.name.match(/\(([^)]+)\)/)
      return match ? match[1] : baseType.name
    },
    formatCurrency(value) {
      return new Intl.NumberFormat('da-DK', {
        style: 'currency',
        currency: this.defaultCurrency || 'DKK',
        minimumFractionDigits: 2,
      }).format(value)
    },
    actionColumn() {
      if (this.$ability.cannot(this.constants.action.permissionLetterTemplateEdit, this.constants.resource.letterTemplate) && this.$ability.cannot(this.constants.action.permissionLetterTemplateDelete, this.constants.resource.letterTemplate)) {
        if (this.user_type !== 'super-admin') {
          this.tableFields.splice(2, 1)
        } else {
          this.tableFields.splice(3, 1)
        }
      }
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

.search_by_select .vs__dropdown-toggle{
  width: 160px;
}

// Cases table styling
#overlay-background {
  .table {
    th, td {
      white-space: nowrap;
      vertical-align: middle;
    }
    .text-right {
      font-variant-numeric: tabular-nums;
    }
    tr {
      cursor: pointer;
    }
  }
}
</style>
